/*
 **
 **      This component translates HTML from WordPress into React components.
 **      This is a passive process:
 **      - replacing custom HTML tags with corresponding React components
 **      - replacing standard HTML tags with eHealth styled tags
 **
 **
 **      THIS COMPONENT MUST NOT USE ANY HOOKS
 **      - NO useEffect
 **      - NO useState
 **      - NO otherHooks
 **
 **      (unless you first confirm that `window` is defined, i.e. this file is running in the browser)
 **      (although this parser is intended for SSG use, by default all scripts run during build *and* client hydration)
 **
 */

import parse, { HTMLReactParserOptions } from 'html-react-parser';
import { Fragment } from 'react';

import SinglePostNtmCta from '~components/SinglePostNtmCta/SinglePostNtmCta';
import Summary from '~components/Summary/Summary';
import { IExtendedDOMNode } from '~globals/interfaces';

import AfterSummaryZipContainer from './PostContentZipContainer/AfterSummaryZipContainer';
import BeforeDesktopBlogFirstHeader from './PostContentZipContainer/BeforeDesktopBlogFirstHeader';
import BeforeMobilePostSecondHeader from './PostContentZipContainer/BeforeMobilePostSecondHeader';
import TopOfArticleZipContainer from './PostContentZipContainer/TopOfArticleZipContainer';
import postContentBlock from './tags/blocks';
import postContentTag from './tags/postTag';

const options = (pathname: string) => {
    const isNtmPage = pathname.includes('/new-to-medicare/');
    return {
        replace: (node: IExtendedDOMNode) => {
            if (node.type === 'text') {
                return node;
            } else if (node.name?.includes('ehealth')) {
                return postContentBlock(node, options(pathname) as HTMLReactParserOptions);
            } else if (node.type === 'tag') {
                return postContentTag(node, options(pathname), isNtmPage);
            }
        },
    };
};

type Props = {
    content: string;
    costData?: { customProperties: { [key: string]: { value: string } } };
    pathname: string;
    isArticlePageRedesign?: boolean;
};

const PostContent = ({ content, costData, pathname, isArticlePageRedesign = false }: Props) => {
    const costDataProps = costData?.customProperties;
    const inlineGlobalVariables: { [key: string]: { value: string } } = {
        ...costDataProps,
    };
    let contentWithGlobalVars = content;

    Object.keys(inlineGlobalVariables).forEach((prop) => {
        if (contentWithGlobalVars.includes(prop)) {
            contentWithGlobalVars = contentWithGlobalVars.replaceAll(
                `{{${prop}}}`,
                inlineGlobalVariables[prop].value
            );
        }
    });

    const toHtml = (c: string, o: HTMLReactParserOptions) => {
        return typeof c === 'string' ? (parse(c, o) as Array<string | JSX.Element>) : c;
    };

    let parsedContent = toHtml(
        contentWithGlobalVars,
        options(pathname) as HTMLReactParserOptions
    )?.filter((e): e is JSX.Element => typeof e === 'object');

    const summary = parsedContent?.filter(
        (e: { props: { class: string }; type: { name: string } }) =>
            e.props.class === 'wp-block-ehealth-summary' || e.type === Summary
    );

    const h2s = parsedContent?.filter((e) => e.type === 'h2');

    const hasMidContentCTA = parsedContent?.some(
        (e) => (e.props as { blockname: string })?.blockname == 'midcontentcta'
    );

    // for each element in the post content, determine if we need to insert some kind of CTA
    parsedContent = parsedContent?.map((e, i) => {
        if (pathname?.match(/\/blog\/.+\/.+\//g) && e === h2s[0]) {
            return <BeforeDesktopBlogFirstHeader key={e.key}>{e}</BeforeDesktopBlogFirstHeader>;
        }
        if (summary.length > 0) {
            if (e === summary[0]) {
                return !isArticlePageRedesign ? (
                    <AfterSummaryZipContainer key={e.key}>{e}</AfterSummaryZipContainer>
                ) : (
                    <Fragment key={e.key}>
                        {e}
                        <TopOfArticleZipContainer />
                    </Fragment>
                );
            } else if (e === h2s[1] && !hasMidContentCTA) {
                return (
                    <Fragment key={e.key}>
                        <SinglePostNtmCta />
                        {e}
                    </Fragment>
                );
            }
        } else {
            if (isArticlePageRedesign && i === 0) {
                return (
                    <Fragment key={e.key}>
                        <TopOfArticleZipContainer />
                        {e}
                    </Fragment>
                );
            } else if (e === h2s[1] && !isArticlePageRedesign) {
                return <BeforeMobilePostSecondHeader key={e.key}>{e}</BeforeMobilePostSecondHeader>;
            } else if (e === h2s[2] && !hasMidContentCTA) {
                return (
                    <Fragment key={e.key}>
                        <SinglePostNtmCta />
                        {e}
                    </Fragment>
                );
            }
        }
        return e;
    });

    return (
        <div className="post-content" data-qa="post-content">
            {parsedContent}
        </div>
    );
};

export default PostContent;
